// Supported site languages. Turkish is the default and lives at the root
// (existing indexed URLs); every other language is served under /<code>/.
export interface Locale {
  code: string // URL prefix and i18next language
  hreflang: string // value for <link rel="alternate" hreflang>
  ogLocale: string // Open Graph locale
  name: string // native name shown in the switcher
  dir: 'ltr' | 'rtl'
  htmlLang: string // <html lang>
  font?: string // extra Google Fonts family for scripts Inter/Manrope do not cover
}

export const DEFAULT_LOCALE = 'tr'

export const LOCALES: Locale[] = [
  { code: 'tr', hreflang: 'tr', ogLocale: 'tr_TR', name: 'Türkçe', dir: 'ltr', htmlLang: 'tr' },
  { code: 'en', hreflang: 'en', ogLocale: 'en_US', name: 'English', dir: 'ltr', htmlLang: 'en' },
  { code: 'de', hreflang: 'de', ogLocale: 'de_DE', name: 'Deutsch', dir: 'ltr', htmlLang: 'de' },
  { code: 'fr', hreflang: 'fr', ogLocale: 'fr_FR', name: 'Français', dir: 'ltr', htmlLang: 'fr' },
  { code: 'es', hreflang: 'es', ogLocale: 'es_ES', name: 'Español', dir: 'ltr', htmlLang: 'es' },
  { code: 'pt', hreflang: 'pt-BR', ogLocale: 'pt_BR', name: 'Português', dir: 'ltr', htmlLang: 'pt-BR' },
  { code: 'ru', hreflang: 'ru', ogLocale: 'ru_RU', name: 'Русский', dir: 'ltr', htmlLang: 'ru' },
  { code: 'ar', hreflang: 'ar', ogLocale: 'ar_AR', name: 'العربية', dir: 'rtl', htmlLang: 'ar', font: 'Noto+Sans+Arabic:wght@400;500;600;700;800' },
  { code: 'zh', hreflang: 'zh-Hans', ogLocale: 'zh_CN', name: '简体中文', dir: 'ltr', htmlLang: 'zh-Hans', font: 'Noto+Sans+SC:wght@400;500;600;700;800' },
  { code: 'ja', hreflang: 'ja', ogLocale: 'ja_JP', name: '日本語', dir: 'ltr', htmlLang: 'ja', font: 'Noto+Sans+JP:wght@400;500;600;700;800' },
  { code: 'hi', hreflang: 'hi', ogLocale: 'hi_IN', name: 'हिन्दी', dir: 'ltr', htmlLang: 'hi', font: 'Noto+Sans+Devanagari:wght@400;500;600;700;800' },
  { code: 'id', hreflang: 'id', ogLocale: 'id_ID', name: 'Bahasa Indonesia', dir: 'ltr', htmlLang: 'id' },
]

export const LOCALE_CODES = LOCALES.map((l) => l.code)

export function getLocale(code: string | undefined): Locale {
  return LOCALES.find((l) => l.code === code) || LOCALES[0]
}

/** URL prefix for a locale: '' for the default, '/en' for the others. */
export function localePrefix(code: string): string {
  return code === DEFAULT_LOCALE ? '' : `/${code}`
}

/** Prefix an in-site path with the locale ('/features' -> '/en/features'). */
export function localizePath(code: string, path: string): string {
  if (!path.startsWith('/') || path.startsWith('//')) return path
  const prefix = localePrefix(code)
  if (!prefix) return path
  return path === '/' ? prefix : prefix + path
}

/** Split a pathname into its locale and the locale-free path. */
export function splitLocale(pathname: string): { code: string; path: string } {
  const m = pathname.match(/^\/([a-z]{2})(?=\/|$)/)
  if (m && m[1] !== DEFAULT_LOCALE && LOCALE_CODES.includes(m[1])) {
    return { code: m[1], path: pathname.slice(m[0].length) || '/' }
  }
  return { code: DEFAULT_LOCALE, path: pathname || '/' }
}
